import React from 'react';
import cx from 'classnames';

import Box from '../../Box';
import LoadingSpinnerNoBits from '../../LoadingSpinnerNoBits';
import BaseButton, { BaseButtonProps } from '../BaseButton';
import Text from '../../Text';
import ChevronRight16 from '../Icon/library/ChevronRight16';
import ChevronRight14 from '../Icon/library/ChevronRight14';
import { PaddingType } from '../../types/padding';

import styles from './Button.module.scss';

const ChevronRightIcon: React.FC<{ size: number; className?: string }> = ({
  size,
  className,
}) => {
  if (size === 32) {
    return <ChevronRight14 className={className} />;
  } else {
    return <ChevronRight16 className={className} />;
  }
};

function outputTextSize(btnSize: number) {
  let textSize;
  switch (btnSize) {
    case 56:
      textSize = 20;
      break;
    case 48:
      textSize = 16;
      break;
    case 40:
      textSize = 14;
      break;
    default:
      textSize = 14;
  }
  return textSize;
}

function outputPadding(btnSize: number) {
  let padding: PaddingType;
  switch (btnSize) {
    case 56:
      padding = 2.25;
      break;
    case 48:
      padding = 2;
      break;
    case 40:
      padding = 1.5;
      break;
    default:
      padding = 1;
  }
  return padding;
}

export interface ButtonProps extends BaseButtonProps {
  /**
   * Escape hatch to give custom styles to specific elements.
   */
  classes?: {
    textClassName?: string;
    iconClassName?: string;
    iconContainerClassName?: string;
  };
  /**
   * Specify the icon that will appear before the text.
   */
  icon?: React.ReactElement | null;
  /**
   * Specify the position of the icon.
   */
  iconPosition?: 'leading' | 'trailing';
}

export const Button = ({
  align = 'left',
  appearance = 'solid',
  className,
  classes,
  children,
  disabled,
  display = 'inline',
  icon,
  iconPosition = 'trailing',
  intent = 'default',
  isLoading = false,
  size = 48,
  ...restProps
}: ButtonProps) => {
  const textSize = outputTextSize(size);
  const padding = outputPadding(size);

  const isIconNull = icon === null;
  const isIconChevronRight = icon === undefined;
  const isAlignLeft = align === 'left';

  return (
    <BaseButton
      className={cx(
        styles.button,
        isLoading && styles.isLoading,
        disabled && styles.isDisabled,
        className,
      )}
      isLoading={isLoading}
      disabled={disabled || isLoading}
      display={display}
      intent={intent}
      appearance={appearance}
      size={size}
      {...restProps}
    >
      <Box display='block' p={padding} style={{ position: 'relative' }}>
        {isLoading && (
          <LoadingSpinnerNoBits
            variant='single-color'
            className={styles.spinnerContainer}
            spinnerClassName={styles.spinner}
          />
        )}
        <Box
          display='flex'
          gap={1}
          flexGrow={isIconChevronRight && !isLoading ? 1 : undefined}
          justifyContent={
            (isAlignLeft && isIconNull) || (isIconChevronRight && isAlignLeft)
              ? 'left'
              : 'center'
          }
          className={styles.content}
        >
          {!isIconNull && !isIconChevronRight && iconPosition === 'leading' && (
            <Box as='span' display='flex' alignSelf='center'>
              {React.cloneElement(icon, {
                contrast: 'inverse-default',
              })}
            </Box>
          )}
          <Text
            size={textSize as any}
            as='span'
            weight='semiBold'
            className={cx(styles.text, classes?.textClassName)}
            style={{
              lineHeight: 1,
              alignSelf: 'center',
              flex: isIconChevronRight ? 1 : 'initial',
            }}
          >
            {children}
          </Text>
          {isIconChevronRight && appearance !== 'ghost' && (
            <Box
              as='span'
              display='flex'
              alignSelf='center'
              className={cx(
                styles.trailingIconContainer,
                classes?.iconContainerClassName,
              )}
            >
              <ChevronRightIcon
                size={size}
                className={cx(styles.icon, classes?.iconClassName)}
              />
            </Box>
          )}
          {!isIconNull && !isIconChevronRight && iconPosition === 'trailing' && (
            <Box as='span' display='flex' alignSelf='center'>
              {React.cloneElement(icon, {
                contrast: 'inverse-default',
              })}
            </Box>
          )}
        </Box>
      </Box>
    </BaseButton>
  );
};

Button.displayName = 'Button';
export default Button;
export { styles };
